import { Plus } from 'lucide-react';
import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import type { OrderWithShipments } from '@/api/types/order-shipments';
import type { Order } from '@/api/types/orders';
import type { Shipment } from '@/api/types/shipments';
import {
  ExpandChevronIcon,
  PrinterIcon,
  TreeBranchIcon,
  TruckIcon,
} from '@/components/icons/shipment-icons';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveLocale } from '@/i18n';
import { formatCurrency } from '@/lib/format/currency';
import { formatDate } from '@/lib/format/date';
import { formatDimensions, formatWeight } from '@/lib/format/measure';
import { cn } from '@/lib/utils';

import { ColumnResizeHandle } from './ColumnResizeHandle';
import { StatusBadge } from './StatusBadge';
import { useColumnWidths } from './useColumnWidths';

/*
 * Default column widths (px) - the Figma table's values. Every header edge
 * except the checkbox column's can be dragged to resize; only the widths the
 * merchant changed are persisted per browser (see useColumnWidths).
 */
const DEFAULT_COLUMN_WIDTHS = {
  select: 71,
  orderNumber: 120,
  shipmentNumber: 160,
  orderId: 150,
  status: 180,
  service: 135,
  reference: 150,
  parcel: 210,
  point: 160,
  price: 110,
  email: 200,
  phone: 140,
  orderedAt: 140,
  shipmentDate: 140,
  actions: 156,
};
type ColumnKey = keyof typeof DEFAULT_COLUMN_WIDTHS;
const COLUMN_KEYS = Object.keys(DEFAULT_COLUMN_WIDTHS) as ColumnKey[];
const COLUMN_WIDTHS_STORAGE_KEY = 'inpost-shipments-column-widths';

const checkboxClasses =
  'size-4 rounded-[4px] border-[#e5e5e5] shadow-none data-[state=checked]:border-primary';
const linkClasses = 'block truncate text-sm font-medium leading-5 text-black underline';
const mutedCellClasses = 'block truncate text-sm leading-5 text-[#737373]';
const plainCellClasses = 'block truncate text-sm leading-5 text-foreground';
const notSelectedBadgeClasses =
  'inline-flex h-6 items-center justify-center whitespace-nowrap rounded-[10px] bg-[#FDECEA] px-2 text-sm font-medium leading-5 text-[#C81E1E]';
const actionButtonClasses =
  'grid size-9 shrink-0 place-items-center rounded-[10px] border border-black bg-transparent hover:bg-accent disabled:pointer-events-none disabled:opacity-50';
const cellClasses = 'h-[52px] overflow-hidden border-b border-[#e5e5e5] p-2';
const headerCellClasses =
  'relative isolate h-10 truncate border-b border-[#e5e5e5] px-2 text-left text-sm font-medium leading-5';

/*
 * Pinned columns (figma's black vertical lines mark the pin boundaries):
 * checkbox + Order ID + Shipment Number stick to the left, Actions to the
 * right. Sticky cells need an explicit opaque background and a z-index so
 * the scrolling middle columns pass underneath. The left offsets of the 2nd
 * and 3rd pinned columns follow the resizable widths, so they are inline
 * styles (`stickyLeft` in the component) rather than classes.
 */
const stickyCheckbox = 'sticky left-0 z-10';
const stickyOrderNumber = 'sticky z-10';
const stickyShipmentNumber = 'sticky z-10 border-r border-r-[#161616]';
const stickyActions = 'sticky right-0 z-10 border-l border-l-[#161616]';

function orderPrice(order: Order, locale: string): string {
  if (order.shopify_total_price === null || order.shopify_currency === null) return '-';
  const value = Number(order.shopify_total_price);
  return Number.isFinite(value) ? formatCurrency(value, order.shopify_currency, locale) : '-';
}

function shipmentLabel(shipment: Shipment): string {
  return shipment.tracking_number ? `#${shipment.tracking_number}` : `ID ${shipment.id}`;
}

/**
 * Weight + dimensions of the shipment's parcel in one line. Rendered from the
 * actual per-shipment values (not a package-template name) so custom package
 * sizes need no changes here.
 */
function parcelSummary(parcel: Shipment['parcel'], locale: string): string {
  const { dimensions, weight } = parcel;
  return `${formatWeight(weight.amount, weight.unit, locale)} · ${formatDimensions(
    dimensions.length,
    dimensions.width,
    dimensions.height,
    dimensions.unit,
    locale,
  )}`;
}

/**
 * Order-level pickup point: the selected point's name (falling back to its id),
 * a red "Not selected" badge when the service needs a point but the buyer left
 * without one, or "-" for door delivery / non-point services.
 */
function PointCell({ order, notSelectedLabel }: { order: Order; notSelectedLabel: string }) {
  const point = order.selected_point_name ?? order.selected_point_id;

  if (point) {
    return <span className={mutedCellClasses}>{point}</span>;
  }

  if (order.requires_point_selection) {
    return <span className={notSelectedBadgeClasses}>{notSelectedLabel}</span>;
  }

  return <span className={mutedCellClasses}>-</span>;
}

function HeaderCell({
  column,
  widths,
  onResize,
  onReset,
  resizeLabel,
  resizable = true,
  className,
  style,
  children,
}: {
  column: ColumnKey;
  widths: Record<ColumnKey, number>;
  onResize: (column: ColumnKey, width: number) => void;
  onReset: (column: ColumnKey) => void;
  resizeLabel: string;
  resizable?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  const width = widths[column];

  return (
    <th className={cn(headerCellClasses, className)} style={{ ...style, width }}>
      {children}
      {resizable ? (
        <ColumnResizeHandle
          width={width}
          label={resizeLabel}
          onResize={(next) => onResize(column, next)}
          onReset={() => onReset(column)}
        />
      ) : null}
    </th>
  );
}

function ActionButtons({
  onPrint,
  printDisabled,
  onTruck,
  printLabel,
  truckLabel,
}: {
  onPrint: () => void;
  printDisabled: boolean;
  onTruck: () => void;
  printLabel: string;
  truckLabel: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label={printLabel}
        title={printLabel}
        disabled={printDisabled}
        onClick={(e) => {
          e.stopPropagation();
          onPrint();
        }}
        className={actionButtonClasses}
      >
        <PrinterIcon className="size-6" />
      </button>
      <button
        type="button"
        aria-label={truckLabel}
        title={truckLabel}
        onClick={(e) => {
          e.stopPropagation();
          onTruck();
        }}
        className={actionButtonClasses}
      >
        <TruckIcon className="size-6" />
      </button>
    </div>
  );
}

/**
 * Merged orders + shipments table (Figma "Shipment Management"). One row per
 * order; multi-shipment orders collapse into an "N packages" row that expands
 * into grey sub-rows, one per shipment. Order-level columns (price, e-mail,
 * phone, ordered at) live on the parent row only; sub-rows carry the
 * shipment-level values plus whatever the collapsed parent leaves empty.
 */
export function OrderShipmentsTable({
  rows,
  isLoading,
  selected,
  onToggleSelect,
  allSelected,
  onToggleSelectAll,
  expanded,
  onToggleExpand,
  onDownloadLabels,
  emptyState,
}: {
  rows: OrderWithShipments[];
  isLoading: boolean;
  selected: Set<number>;
  onToggleSelect: (orderId: number) => void;
  allSelected: boolean;
  onToggleSelectAll: () => void;
  expanded: Set<number>;
  onToggleExpand: (orderId: number) => void;
  onDownloadLabels: (shipments: Shipment[]) => void;
  emptyState: ReactNode;
}) {
  const { t, i18n } = useTranslation('shipments');
  const locale = resolveLocale(i18n.language);
  const navigate = useNavigate();
  const { widths, setWidth, resetWidth } = useColumnWidths(
    COLUMN_WIDTHS_STORAGE_KEY,
    DEFAULT_COLUMN_WIDTHS,
  );

  const totalWidth = COLUMN_KEYS.reduce((sum, key) => sum + widths[key], 0);
  const stickyLeft = {
    orderNumber: widths.select,
    shipmentNumber: widths.select + widths.orderNumber,
  };
  const headerProps = {
    widths,
    onResize: setWidth,
    onReset: resetWidth,
    resizeLabel: t('table.resizeColumn'),
  };

  return (
    <div className="overflow-x-auto rounded-[10px] border border-[#e5e5e5]">
      <table
        className="min-w-full table-fixed border-separate border-spacing-0"
        style={{ width: totalWidth }}
      >
        <thead>
          <tr>
            <HeaderCell
              column="select"
              resizable={false}
              className={cn(stickyCheckbox, 'bg-white')}
              {...headerProps}
            >
              <div className="flex justify-center">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onToggleSelectAll}
                  aria-label={t('table.selectAll')}
                  className={checkboxClasses}
                />
              </div>
            </HeaderCell>
            <HeaderCell
              column="orderNumber"
              className={cn(stickyOrderNumber, 'bg-white')}
              style={{ left: stickyLeft.orderNumber }}
              {...headerProps}
            >
              {t('columns.orderNumber')}
            </HeaderCell>
            <HeaderCell
              column="shipmentNumber"
              className={cn(stickyShipmentNumber, 'bg-white')}
              style={{ left: stickyLeft.shipmentNumber }}
              {...headerProps}
            >
              {t('columns.shipmentNumber')}
            </HeaderCell>
            <HeaderCell column="orderId" {...headerProps}>
              {t('columns.orderId')}
            </HeaderCell>
            <HeaderCell column="status" {...headerProps}>
              {t('columns.status')}
            </HeaderCell>
            <HeaderCell column="service" {...headerProps}>
              {t('columns.service')}
            </HeaderCell>
            <HeaderCell column="reference" {...headerProps}>
              {t('columns.reference')}
            </HeaderCell>
            <HeaderCell column="parcel" {...headerProps}>
              {t('columns.parcel')}
            </HeaderCell>
            <HeaderCell column="point" {...headerProps}>
              {t('columns.point')}
            </HeaderCell>
            <HeaderCell column="price" {...headerProps}>
              {t('columns.price')}
            </HeaderCell>
            <HeaderCell column="email" {...headerProps}>
              {t('columns.email')}
            </HeaderCell>
            <HeaderCell column="phone" {...headerProps}>
              {t('columns.phone')}
            </HeaderCell>
            <HeaderCell column="orderedAt" {...headerProps}>
              {t('columns.orderedAt')}
            </HeaderCell>
            <HeaderCell column="shipmentDate" {...headerProps}>
              {t('columns.shipmentDate')}
            </HeaderCell>
            <HeaderCell column="actions" className={cn(stickyActions, 'bg-white')} {...headerProps}>
              {t('columns.actions')}
            </HeaderCell>
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 5 }, (_, i) => (
                <tr key={i}>
                  {COLUMN_KEYS.map((key) => (
                    <td key={key} className={cn(cellClasses, 'bg-white')}>
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            : null}

          {!isLoading && rows.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_KEYS.length} className="p-2">
                {emptyState}
              </td>
            </tr>
          ) : null}

          {!isLoading &&
            rows.map(({ order, shipments }) => {
              const isMulti = shipments.length > 1;
              const isExpanded = isMulti && expanded.has(order.id);
              const single = shipments.length === 1 ? shipments[0] : null;
              const isSelected = selected.has(order.id);
              const service =
                order.shipping_method_title ??
                (order.service_type ? t(`serviceTypes.${order.service_type}`) : null);
              const phone = order.shopify_phone ?? order.shopify_shipping_address?.phone ?? null;
              // Collapsed multi-shipment rows summarise status as the first
              // shipment's badge plus "+N", where N is the number of *other*
              // distinct status groups among the order's shipments (0 = all the same).
              const extraStatusCount =
                new Set(shipments.map((s) => s.carrier_status_group ?? 'new')).size - 1;

              return (
                <Fragment key={order.id}>
                  <tr
                    className={cn(isMulti && 'cursor-pointer')}
                    onClick={isMulti ? () => onToggleExpand(order.id) : undefined}
                  >
                    <td className={cn(cellClasses, stickyCheckbox, 'bg-white')}>
                      <div className="flex justify-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onToggleSelect(order.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('table.selectRow')}
                          className={checkboxClasses}
                        />
                      </div>
                    </td>
                    <td
                      className={cn(cellClasses, stickyOrderNumber, 'bg-white')}
                      style={{ left: stickyLeft.orderNumber }}
                    >
                      <Link
                        to={`/orders/${order.shopify_order_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className={linkClasses}
                      >
                        {order.shopify_order_number}
                      </Link>
                    </td>
                    <td
                      className={cn(cellClasses, stickyShipmentNumber, 'bg-white')}
                      style={{ left: stickyLeft.shipmentNumber }}
                    >
                      {shipments.length === 0 ? (
                        <Link
                          to={`/orders/${order.shopify_order_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex h-9 w-fit items-center gap-2 rounded-[10px] bg-primary px-4 text-sm font-medium leading-5 text-black"
                        >
                          <Plus className="size-4" />
                          {t('table.add')}
                        </Link>
                      ) : null}
                      {single ? (
                        <Link
                          to={`/orders/${order.shopify_order_id}?shipment=${single.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className={linkClasses}
                        >
                          {shipmentLabel(single)}
                        </Link>
                      ) : null}
                      {isMulti ? (
                        <span className="inline-flex h-6 items-center justify-center gap-1 whitespace-nowrap rounded-[10px] bg-[#f7f7f7] px-2 text-sm font-medium leading-5 text-foreground">
                          {t('table.packages', { count: shipments.length })}
                        </span>
                      ) : null}
                    </td>
                    <td className={cellClasses}>
                      <Link
                        to={`/orders/${order.shopify_order_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className={linkClasses}
                      >
                        #{order.shopify_order_id}
                      </Link>
                    </td>
                    <td className={cellClasses}>
                      {isExpanded ? null : isMulti ? (
                        <div className="flex items-center gap-1.5">
                          <StatusBadge group={shipments[0].carrier_status_group ?? 'new'} />
                          {extraStatusCount > 0 ? (
                            <span className="text-sm font-medium leading-5 text-[#737373]">
                              +{extraStatusCount}
                            </span>
                          ) : null}
                        </div>
                      ) : single ? (
                        <StatusBadge group={single.carrier_status_group ?? 'new'} />
                      ) : (
                        <StatusBadge group="new" />
                      )}
                    </td>
                    <td className={cellClasses}>
                      {!isExpanded && service ? (
                        <span className={mutedCellClasses}>{service}</span>
                      ) : null}
                    </td>
                    <td className={cellClasses}>
                      {single ? (
                        <span className={mutedCellClasses}>{single.reference ?? '-'}</span>
                      ) : null}
                    </td>
                    <td className={cellClasses}>
                      {single ? (
                        <span
                          className={mutedCellClasses}
                          title={parcelSummary(single.parcel, locale)}
                        >
                          {parcelSummary(single.parcel, locale)}
                        </span>
                      ) : null}
                    </td>
                    <td className={cellClasses}>
                      <PointCell order={order} notSelectedLabel={t('point.notSelected')} />
                    </td>
                    <td className={cellClasses}>
                      <span className={plainCellClasses}>{orderPrice(order, locale)}</span>
                    </td>
                    <td className={cellClasses}>
                      <span className={mutedCellClasses}>{order.shopify_email ?? '-'}</span>
                    </td>
                    <td className={cellClasses}>
                      <span className={mutedCellClasses}>{phone ?? '-'}</span>
                    </td>
                    <td className={cellClasses}>
                      <span className={plainCellClasses}>
                        {order.shopify_created_at
                          ? formatDate(order.shopify_created_at, locale)
                          : '-'}
                      </span>
                    </td>
                    <td className={cellClasses}>
                      {single ? (
                        <span className={plainCellClasses}>
                          {formatDate(single.created_at, locale)}
                        </span>
                      ) : null}
                    </td>
                    <td className={cn(cellClasses, stickyActions, 'bg-white')}>
                      <div className="flex items-center justify-between gap-2">
                        <ActionButtons
                          printLabel={t('actions.downloadLabel')}
                          truckLabel={t('actions.manageDelivery')}
                          printDisabled={shipments.length === 0}
                          onPrint={() => onDownloadLabels(shipments)}
                          onTruck={() => navigate(`/orders/${order.shopify_order_id}`)}
                        />
                        {isMulti ? (
                          <button
                            type="button"
                            aria-label={isExpanded ? t('table.collapse') : t('table.expand')}
                            aria-expanded={isExpanded}
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleExpand(order.id);
                            }}
                            className="shrink-0"
                          >
                            <ExpandChevronIcon
                              className={cn(
                                'size-6 transition-transform',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>

                  {isExpanded
                    ? shipments.map((shipment, index) => {
                        const isLast = index === shipments.length - 1;
                        const subCell = cn(
                          'h-[52px] overflow-hidden p-2',
                          isLast ? 'border-b border-black' : 'border-b border-[#e5e5e5]',
                        );

                        return (
                          <tr key={shipment.id} className="bg-[#f6f6f6]">
                            <td className={cn(subCell, stickyCheckbox, 'bg-[#f6f6f6]')}>
                              <div className="relative flex justify-center">
                                <TreeBranchIcon className="absolute left-[1px] top-1/2 size-4 -translate-y-1/2" />
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => onToggleSelect(order.id)}
                                  aria-label={t('table.selectRow')}
                                  className={checkboxClasses}
                                />
                              </div>
                            </td>
                            <td
                              className={cn(subCell, stickyOrderNumber, 'bg-[#f6f6f6]')}
                              style={{ left: stickyLeft.orderNumber }}
                            >
                              <Link
                                to={`/orders/${order.shopify_order_id}`}
                                className={linkClasses}
                              >
                                {order.shopify_order_number}
                              </Link>
                            </td>
                            <td
                              className={cn(subCell, stickyShipmentNumber, 'bg-[#f6f6f6]')}
                              style={{ left: stickyLeft.shipmentNumber }}
                            >
                              <Link
                                to={`/orders/${order.shopify_order_id}?shipment=${shipment.id}`}
                                className={linkClasses}
                              >
                                {shipmentLabel(shipment)}
                              </Link>
                            </td>
                            <td className={subCell} />
                            <td className={subCell}>
                              <StatusBadge group={shipment.carrier_status_group ?? 'new'} />
                            </td>
                            <td className={subCell}>
                              {service ? <span className={mutedCellClasses}>{service}</span> : null}
                            </td>
                            <td className={subCell}>
                              <span className={mutedCellClasses}>{shipment.reference ?? '-'}</span>
                            </td>
                            <td className={subCell}>
                              <span
                                className={mutedCellClasses}
                                title={parcelSummary(shipment.parcel, locale)}
                              >
                                {parcelSummary(shipment.parcel, locale)}
                              </span>
                            </td>
                            <td className={subCell} />
                            <td className={subCell} />
                            <td className={subCell} />
                            <td className={subCell} />
                            <td className={subCell} />
                            <td className={subCell}>
                              <span className={plainCellClasses}>
                                {formatDate(shipment.created_at, locale)}
                              </span>
                            </td>
                            <td className={cn(subCell, stickyActions, 'bg-[#f6f6f6]')}>
                              <ActionButtons
                                printLabel={t('actions.downloadLabel')}
                                truckLabel={t('actions.viewShipment')}
                                printDisabled={false}
                                onPrint={() => onDownloadLabels([shipment])}
                                onTruck={() =>
                                  navigate(
                                    `/orders/${order.shopify_order_id}?shipment=${shipment.id}`,
                                  )
                                }
                              />
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
